import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import LoadingSpinner from "@/components/loading-spinner/LoadingSpinner";
import FAQSearchForm from "../../components/faq-search-form/FAQSearchForm";
import Solution from "../../components/solution/Solution";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import FAQFeedbackForm from "../../components/faq-feedback-form/FAQFeedbackForm";
import FAQMoreHelp from "../../components/faq-more-help/FAQMoreHelp";
import FAQRelatedSolutions from "../../components/faq-related-solutions/FAQRelatedSolutions";
import { getIdFromSolutionSlug } from "../../utils/solutionSlug";
import { dispatchNewEvent } from "../../utils/analytics/trigger/analyticsTrigger";
import faqClient from "../../clients/faqClient";

// Styles
import styles from "./faq.module.scss";

function ErrorMessage({ code = "" }) {
  switch (code) {
    case "resource.not.found":
      return (
        <p className={styles.faq__error}>
          Sorry, we couldn’t find the article you were looking for. Try a keyword search or go back to look at the
          topics on our{" "}
          <Link data-test="customer-services-link" href="/">
            Customer Services page
          </Link>
          .
        </p>
      );
    case "internal.server.error":
    default:
      return (
        <p className={styles.faq__error}>
          Sorry, we’re having a technical problem. Try again later or go back to look at the topics on our{" "}
          <Link data-test="customer-services-link" href="/">
            Customer Services page
          </Link>
          .
        </p>
      );
  }
}

export const metadata = {
  title: `FAQ | Customer Services`,
  description: `Have a question? We're here to help with all your queries, from delivery and returns to orders and your online account and your My John Lewis membership.`,
};

function FAQAnswer() {
  const {
    query: { q, slug },
    push,
  } = useRouter();
  const [query, setQuery] = useState(q);
  const [solution, setSolution] = useState(null);
  const [error, setError] = useState(null);
  const [solutionLoading, setSolutionLoading] = useState(true);
  const [related, setRelated] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const solutionId = getIdFromSolutionSlug(slug);
  const showRelated = !relatedLoading && related && related.length > 0;
  const fetchSolution = async () => {
    try {
      const faqClientInstance = await faqClient();
      const response = await faqClientInstance.get(`/solutions/${solutionId}`);
      return { solution: response.data };
    } catch (err) {
      return { error: err.response && err.response.data };
    }
  };

  const fetchRelated = async () => {
    try {
      const faqClientInstance = await faqClient();
      const response = await faqClientInstance.get(`/solutions/${solutionId}/related`);
      return response.data.relatedSolutions.slice(0, 5).map(({ id, description: title }) => ({ id, title }));
    } catch {
      return [];
    }
  };

  const fetchNewRelated = () => {
    setRelatedLoading(true);
    fetchRelated().then(result => {
      setRelated(result);
      setRelatedLoading(false);
    });
  };

  const fetchNewSolution = () => {
    setSolutionLoading(true);
    setRelated(null);
    setRelatedLoading(false);

    fetchSolution().then(result => {
      setSolution(result.solution);
      setError(result.error);
      setSolutionLoading(false);
      if (!result.error && result.solution) {
        fetchNewRelated();
      }
    });
  };

  useEffect(() => {
    dispatchNewEvent("dataStateChange", {
      action: "faq.Event",
      type: "answerViewed",
      answerId: solutionId,
    });
    fetchNewSolution();
  }, [solutionId]);

  const handleSubmit = event => {
    event.preventDefault();
    if (query !== "") {
      push(`/faq?q=${query}`);
    }
  };

  const handleFeedback = async ({ solved, comment }) => {
    dispatchNewEvent("dataStateChange", {
      action: "faq.Event",
      type: "feedbackSubmitted",
      feedback: solved,
      comment: comment ? "1" : "0",
    });

    await faqClient()
      .post(`/solutions/${solutionId}/feedback`, {
        solved: solved === "yes",
        comments: comment,
      })
      .catch();
  };

  return (
    <>
      <Breadcrumb links={[{ text: "Customer services", href: "/" }]} />
      <div className="util-container">
        <div className={styles.faq__form}>
          <FAQSearchForm query={query} setQuery={setQuery} onSubmit={handleSubmit} />
          {solutionLoading && (
            <div className={styles.faq__spinner}>
              <LoadingSpinner />
            </div>
          )}
          {!solutionLoading && solution && (
            <>
              <Solution
                className={styles.faq__solution}
                titleClassName={styles["faq__solution-title"]}
                solution={solution}
              />
              <FAQFeedbackForm onSubmit={handleFeedback} solution={solution.id} />
              {relatedLoading && (
                <div className={styles.faq__spinner}>
                  <LoadingSpinner />
                </div>
              )}
              {showRelated && <FAQRelatedSolutions solutions={related} className={styles["faq__related-solutions"]} />}
              <FAQMoreHelp />
            </>
          )}
          {!solutionLoading && error && <ErrorMessage {...error} />}
        </div>
      </div>
    </>
  );
}

FAQAnswer.metadata = metadata;

export default FAQAnswer;
